import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TabViewModule } from 'primeng/tabview';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessagesModule } from 'primeng/messages';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { Router, ActivatedRoute } from '@angular/router';
import { FormGroup, ReactiveFormsModule, FormBuilder, FormArray } from '@angular/forms';

import { BasicInfoComponent } from './tabs/basic-info/basic-info.component';
import { RankingsComponent } from './tabs/rankings/rankings.component';
import { UndergraduateComponent } from './tabs/undergraduate/undergraduate.component';
import { MastersDoctoralComponent } from './tabs/masters-doctoral/masters-doctoral.component';
import { CoursesComponent } from './tabs/courses/courses.component';
import { AddInstituteService } from '../../services/super-admin/add-institute.service';
import { apiResponse } from '../../models/apiResponse';
import { InstituteCommonDetailsResponse } from '../../models/Institutedetails/InstituteCommonDetailsResponse';

@Component({
    selector: 'app-add-institute',
    standalone: true,
    imports: [
        CommonModule,
        ButtonModule,
        TabViewModule,
        ProgressSpinnerModule,
        MessagesModule,
        ToastModule,
        ReactiveFormsModule,
        BasicInfoComponent,
        RankingsComponent,
        UndergraduateComponent,
        MastersDoctoralComponent,
        CoursesComponent
    ],
    providers: [MessageService],
    templateUrl: './add-institute.component.html',
    styleUrls: ['./add-institute.component.scss']
})
export class AddInstituteComponent implements OnInit {
    instituteForm: FormGroup;
    /** Used for Save & Next / Update & Next / Finish button */
    loading: boolean = false;
    /** Used while loading institute details to fill form (edit mode) */
    loadingFormData: boolean = false;
    activeIndex: number = 0;
    isEditMode: boolean = false;
    isAddModeSession: boolean = false;
    instituteId: string | null = null;
    branchCampusesData: string[] = [];
    alumniData: any = null;

    @ViewChild('basicInfoTab') basicInfoTab!: BasicInfoComponent;
    @ViewChild('rankingsTab') rankingsTab!: RankingsComponent;
    @ViewChild('undergraduateTab') undergraduateTab!: UndergraduateComponent;
    @ViewChild('mastersDoctoralTab') mastersDoctoralTab!: MastersDoctoralComponent;
    @ViewChild('coursesTab') coursesTab!: CoursesComponent;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private fb: FormBuilder,
        private messageService: MessageService,
        private addInstituteService: AddInstituteService
    ) {
        this.instituteForm = this.fb.group({
            basicInfo: this.fb.group({}),
            rankings: this.fb.group({
                id: [0],
                qsRanking: [''],
                countryRanking: [null],
                usNewsRanking: [null],
                indiaTodayRanking: [''],
                nirfRanking: [''],
                subjectRanks: [null],
                chill: [false],
                competitive: [false],
                startup: [false],
                research: [false],
                creative: [false],
                traditional: [false]
            }),
            undergraduate: this.fb.group({}),
            mastersDoctoral: this.fb.group({}),
            courses: this.fb.array([])
        });
    }

    ngOnInit() {
        this.route.params.subscribe(params => {
            const id = params['id'];
            if (id) {
                this.isEditMode = true;
                this.isAddModeSession = false;
                this.instituteId = id;
                this.loadInstituteDetails(id);
            } else {
                this.isAddModeSession = true;
            }
        });
    }

  private loadInstituteDetails(id: string) {
    this.loadingFormData = true;
    this.addInstituteService.getInstituteCommonDetails(id).subscribe({
        next: (response: apiResponse<InstituteCommonDetailsResponse>) => {
            this.loadingFormData = false;
            const data = response.result || response as any;
            if (data) {
                this.basicInfoForm.patchValue(data);

                if (data.facilities) this.basicInfoForm.patchValue(data.facilities);
                if (data.instituteActivities) this.basicInfoForm.patchValue(data.instituteActivities);
                if (data.researchFacilities) this.basicInfoForm.patchValue(data.researchFacilities);
                if (data.alumni) this.alumniData = data.alumni;

                if (data.vibes) {
                    const vibeObj = {
                        chill: data.vibes.chill ?? false,
                        competitive: data.vibes.competitive ?? false,
                        startup: data.vibes.startup ?? false,
                        research: data.vibes.research ?? false,
                        creative: data.vibes.creative ?? false,
                        traditional: data.vibes.traditional ?? false
                    };
                    this.basicInfoForm.patchValue(vibeObj);
                    this.rankingsForm.patchValue(vibeObj);
                }
                this.rankingsForm.patchValue(data);

                if (data.nameOfBranchCampuses?.length > 0) {
                    this.branchCampusesData = [...data.nameOfBranchCampuses];
                }
            }
        },
        error: (err: any) => {
            this.loadingFormData = false;
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load institute details.'
            });
        }
    });
}
    get basicInfoForm(): FormGroup { return this.instituteForm.get('basicInfo') as FormGroup; }
    get rankingsForm(): FormGroup { return this.instituteForm.get('rankings') as FormGroup; }
    get undergraduateForm(): FormGroup { return this.instituteForm.get('undergraduate') as FormGroup; }
    get mastersDoctoralForm(): FormGroup { return this.instituteForm.get('mastersDoctoral') as FormGroup; }
    get coursesArray(): FormArray { return this.instituteForm.get('courses') as FormArray; }

    goBack() {
        this.router.navigate(['/admin/colleges']);
    }

    save() {
        this.loading = true;
        switch (this.activeIndex) {
            case 0:
                this.basicInfoTab.save();
                break;
            case 1:
                this.rankingsTab.save();
                break;
            case 2:
                this.undergraduateTab.save();
                break;
            case 3:
                this.mastersDoctoralTab.save();
                break;
            case 4:
                this.coursesTab.save();
                break;
            default:
                this.loading = false;
                break;
        }
    }

    handleSaveSuccess(event: any) {
        this.loading = false;

        if (event.mode === 'add' || event.mode === 'edit') {
            const data = event.response?.result || event.response;

            if (this.activeIndex === 0 && data) {
                const newId = data.instituteId || data.Id || data.id;
                if (newId) {
                    this.instituteId = String(newId);
                    this.isEditMode = true;
                }
            } else if (!this.instituteId && data) {
                const newId = data.instituteId || data.Id || data.id;
                if (newId) {
                    this.instituteId = String(newId);
                    this.isEditMode = true;
                }
            }


            let message = '';
            switch (this.activeIndex) {
                case 0: message = 'Basic Information ' + (event.mode === 'edit' ? 'updated' : 'saved') + ' successfully!'; break;
                case 1: message = 'Rankings updated successfully!'; break;
                case 2: message = 'Undergraduate details ' + (event.mode === 'edit' ? 'updated' : 'saved') + ' successfully!'; break;
                case 3: message = 'Masters/Doctoral details ' + (event.mode === 'edit' ? 'updated' : 'saved') + ' successfully!'; break;
                case 4: message = 'All courses ' + (event.mode === 'edit' ? 'updated' : 'saved') + ' successfully!'; break;
            }

            this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: message
            });

            if (this.activeIndex < 4) {
                this.activeIndex++;
            } else {
                this.router.navigate(['/admin/colleges']);
            }
        }
    }

    handleSaveError(err: any) {
        this.loading = false;
        if (err.validationError) {
            this.showValidationError(err.fields);
        } else {
            console.error('Save Error:', err);
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: err.message || 'An error occurred while saving.'
            });
        }
    }

    private showValidationError(fields?: string[]) {
        let detail = 'Please fill in all required fields.';


        this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: detail,
            life: 5000
        });
    }

    preview() {
        const data = this.instituteForm.value;
        console.log('Previewing Institute Data:', data);
        this.messageService.add({
            severity: 'info',
            summary: 'Preview',
            detail: 'Check console for current form data structure.'
        });
    }
}
